import React, { useMemo, useState } from 'react';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import { Box, Stack, Typography } from '@mui/material';
import ArrowRightAltSharpIcon from '@mui/icons-material/ArrowRightAltSharp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { useTailwindBrand } from '../../shared';
import { capitalizeFirstLetter, fullNameChild } from '../../../Utils/Function';
import Functions from '../../../Helpers/Functions';
import {
    getActiveBrandConfig,
    getActivePrestationBrandConfig,
} from '../../../config/environment.config';
import ButtonField from '../../Ui/ButtonField';

interface Props {
    courses: any[];
    teachr?: any;
    /** Number of days to show initially before "Voir plus". Default 3 */
    initialDays?: number;
    /** Hide the "Voir tous les cours" footer button */
    hideFooter?: boolean;
}

const getDayColor = (dateString: string, primary: string) => {
    const date = moment(dateString).startOf('day');
    if (date.isSame(moment().startOf('day'))) return primary;
    if (date.isSame(moment().add(1, 'day').startOf('day'))) return '#16a34a';
    return '#6b7280';
};

const getDayLabel = (dateString: string) => {
    const date = moment(dateString);
    if (date.isSame(moment(), 'day')) return "Aujourd'hui";
    if (date.isSame(moment().add(1, 'day'), 'day')) return 'Demain';
    return capitalizeFirstLetter(date.format('dddd DD MMM'));
};

const UpcomingCalendar: React.FC<Props> = ({
    courses,
    initialDays = 3,
    hideFooter = false,
}) => {
    const { brandConfig } = useTailwindBrand();
    const navigate = useNavigate();
    const primary = brandConfig.colors.primary;
    const ff = brandConfig.fontFamily;
    const [expanded, setExpanded] = useState(false);

    const groupedCourses = useMemo(() => {
        const grouped: Record<string, any[]> = {};
        courses?.forEach((course) => {
            const key = moment(course.startDatetime).format('YYYY-MM-DD');
            if (!grouped[key]) grouped[key] = [];
            grouped[key].push(course);
        });
        return grouped;
    }, [courses]);

    const sortedDays = useMemo(
        () =>
            Object.keys(groupedCourses).sort((a, b) => {
                const today = moment().startOf('day');
                const tomorrow = moment().add(1, 'day').startOf('day');
                const aDate = moment(a).startOf('day');
                const bDate = moment(b).startOf('day');
                if (aDate.isSame(today)) return -1;
                if (bDate.isSame(today)) return 1;
                if (aDate.isSame(tomorrow)) return -1;
                if (bDate.isSame(tomorrow)) return 1;
                return aDate.diff(bDate);
            }),
        [groupedCourses]
    );

    const hasMore = sortedDays.length > initialDays;
    const visibleDays = expanded ? sortedDays : sortedDays.slice(0, initialDays);
    const hiddenCount = sortedDays.length - initialDays;

    if (!courses || courses.length === 0) {
        return (
            <Box sx={{ textAlign: 'center', py: 5, px: 2 }}>
                <Box sx={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    width: 56, height: 56, borderRadius: '50%', mb: 1.5,
                    bgcolor: `${primary}14`,
                }}>
                    <span style={{ fontSize: '1.6rem' }}>📭</span>
                </Box>
                <Typography variant="body2" fontWeight={600} sx={{ fontFamily: ff, color: primary }}>
                    Aucune prestation à venir
                </Typography>
                <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5, fontFamily: ff }}>
                    Vos prochaines {getActivePrestationBrandConfig('s')} apparaîtront ici
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
                {visibleDays.map((dayKey, dayIndex) => {
                    const dayColor = getDayColor(dayKey, primary);
                    const isToday = dayColor === primary;

                    return (
                        <Box key={dayKey} sx={{ mb: dayIndex < visibleDays.length - 1 ? 2.5 : 1 }}>
                            {/* Day header */}
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                                <Box sx={{ width: 3, height: 16, borderRadius: 4, bgcolor: dayColor, flexShrink: 0 }} />
                                <Typography variant="caption" fontWeight={700} sx={{
                                    fontFamily: ff, color: dayColor,
                                    textTransform: 'uppercase', letterSpacing: '0.6px', fontSize: '0.68rem',
                                }}>
                                    {getDayLabel(dayKey)}
                                </Typography>
                                <Box sx={{ px: 0.9, py: 0.1, borderRadius: 10, bgcolor: `${dayColor}18` }}>
                                    <Typography variant="caption" sx={{ fontSize: '0.6rem', fontWeight: 700, color: dayColor, fontFamily: ff }}>
                                        {groupedCourses[dayKey].length}
                                    </Typography>
                                </Box>
                            </Stack>

                            {/* Course rows */}
                            <Stack spacing={0.5}>
                                {groupedCourses[dayKey].map((course, index) => {
                                    const time = moment(course.startDatetime).format('HH:mm');
                                    const duration = Functions.renderDuration(course.duration);
                                    const fullName = fullNameChild(
                                        course?.forChildFirstName ?? '',
                                        course?.forChildLastName ?? ''
                                    );

                                    return (
                                        <Stack
                                            key={`${dayKey}-${index}`}
                                            direction="row"
                                            alignItems="center"
                                            sx={{
                                                px: 1.2, py: 0.75, borderRadius: 1.5,
                                                bgcolor: isToday ? `${primary}07` : 'grey.50',
                                                borderLeft: '3px solid',
                                                borderColor: dayColor,
                                                transition: 'all 0.15s',
                                                '&:hover': { bgcolor: `${primary}0e` },
                                            }}
                                        >
                                            <Typography variant="caption" fontWeight={600} noWrap sx={{ fontFamily: ff, flex: 1, minWidth: 0, fontSize: '0.78rem' }}>
                                                {capitalizeFirstLetter(fullName) || '-'}
                                            </Typography>
                                            <Typography variant="caption" fontWeight={700} sx={{ fontFamily: ff, color: dayColor, flexShrink: 0, fontSize: '0.72rem' }}>
                                                {time}
                                            </Typography>
                                            <Typography variant="caption" sx={{ fontFamily: ff, color: 'text.disabled', mx: 0.5, fontSize: '0.65rem' }}>
                                                {duration}
                                            </Typography>
                                        </Stack>
                                    );
                                })}
                            </Stack>
                        </Box>
                    );
                })}
            </Box>

            {/* Expand / collapse if more days exist */}
            {hasMore && (
                <Box
                    onClick={() => setExpanded((v) => !v)}
                    sx={{
                        mx: 2, mb: 1, px: 2, py: 0.8, borderRadius: 2,
                        cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5,
                        bgcolor: `${primary}08`, border: '1px dashed', borderColor: `${primary}33`,
                        transition: 'all 0.15s',
                        '&:hover': { bgcolor: `${primary}14` },
                    }}
                >
                    {expanded
                        ? <KeyboardArrowUpIcon sx={{ fontSize: 16, color: primary }} />
                        : <KeyboardArrowDownIcon sx={{ fontSize: 16, color: primary }} />}
                    <Typography variant="caption" fontWeight={700} sx={{ fontFamily: ff, color: primary, fontSize: '0.72rem' }}>
                        {expanded ? 'Réduire' : `${hiddenCount} jour${hiddenCount > 1 ? 's' : ''} de plus`}
                    </Typography>
                </Box>
            )}

            {!hideFooter && (
                <Box sx={{ px: 2, pt: 1, pb: 1.5 }}>
                    <ButtonField
                        label={`Voir ${getActivePrestationBrandConfig('s', 'tous les ', 'toutes les ')}`}
                        variant="outlined"
                        fullWidth
                        endIcon={<ArrowRightAltSharpIcon />}
                        onClick={() => navigate(`/${getActiveBrandConfig()}/tableau-de-bord/${getActivePrestationBrandConfig()}`)}
                    />
                </Box>
            )}
        </Box>
    );
};

export default UpcomingCalendar;
